/*  MUI  */
import {
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import IntegrationEnabled from 'application/Shared/Components/IntegrationEnabled';
import { UserPicker } from 'application/Shared/Components/Pickers';
import { Defaults } from 'application/Shared/Constants';
import { Helpers, Notify, Transition } from 'application/Shared/Helpers';
import { Modal, ModalBody, ModalFooter } from 'application/Shared/Modals';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

/*  Components  */
import { ReportSummary } from './ReportSummary';

/*  State  */
const state = () => ({
    cwManage: false,
    loaded: false,
    name: '',
    notes: '',
    open: false,
    reportSetup: {
        type: null
    },
    user: null
});

class SaveAndSchedule extends Component {
    constructor(props) {
        super(props);
        this.state = state();
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unload();
    }

    load = () => {
        const { reportSetup } = this.props;
        this.setState({ loaded: true, reportSetup });
    };

    unload = () => {
        this.setState(state());
    };

    save = async () => {
        const { match, t, savedReportParams } = this.props;
        const { clientId } = match.params;
        const { name, cwManage, notes, user } = this.state;

        if (!user) {
            Notify.e(t('Reports.Schedule.error_select_user'));
            return;
        }

        const payload = {
            emailPlatformUserId: user,
            name,
            notes,
            params: savedReportParams,
            subscriberType: cwManage ? 'cwManage' : 'user'
        };
        if (clientId) {
            payload.platformTenantId = clientId;
        }

        if (!Helpers.isUUID(user)) {
            if (!Helpers.isEmail(user)) {
                const err = 'Invalid User/Email';
                debug.e(err, user);
                Notify.e(err);
                return;
            }

            payload.emailPlatformUserId = Defaults.id0;
            payload.email = user;
        }

        try {
            await axios.post('/scheduledReport/', payload);
        } catch (e) {
            debug.e('Unable to schedule report', e);
            Notify.e(t('Reports.Schedule.error'));
            return;
        }

        Notify.s(t('Reports.Schedule.success'));
        this.setState({ open: false });
    };

    renderButton = () => {
        const { t, session } = this.props;
        const { reportSetup } = this.state;
        const { id } = session;
        const scheduleReportOption = reportSetup.type !== 'offboard';

        return (
            scheduleReportOption && (
                <Button
                    className="hidePrint"
                    onClick={() => this.setState({ open: true, user: id })}
                    disabled={!reportSetup.type}
                    variant="contained"
                    color="primary"
                >
                    {t('Reports.save_and_schedule')}
                </Button>
            )
        );
    };

    renderUserSelect = () => {
        const { t } = this.props;
        const { user } = this.state;

        return (
            <Grid item xs={12}>
                <Typography color="textSecondary" fontWeight={600}>
                    {t('Reports.Schedule.select_user_email')}
                </Typography>
                <br />
                <UserPicker
                    multiple={false}
                    value={user}
                    hasValidEmail
                    freeInput
                    inputValue={user}
                    onChange={(v) => this.setState({ user: v })}
                    onClose={(e) => {
                        const { value } = e.target;
                        if (value) {
                            this.setState({ user: value });
                        }
                    }}
                />
                <br />
            </Grid>
        );
    };

    renderContent = () => {
        const { t } = this.props;
        const { reportSetup, name, cwManage, notes } = this.state;
        const { type } = reportSetup;

        return (
            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <hr />
                    <ReportSummary
                        noDates
                        expanded
                        reportSetup={reportSetup}
                        title=""
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Report Name"
                        onChange={(e) =>
                            this.setState({ name: e.target.value })
                        }
                        autoComplete="off"
                        autoFocus
                        fullWidth
                        value={name}
                    />
                </Grid>
                <Grid item xs={12}>
                    <IntegrationEnabled integrationKey="cw_manage">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={cwManage}
                                    onChange={(e) =>
                                        this.setState({
                                            cwManage: e.target.checked
                                        })
                                    }
                                    label="Send to ConnectWise Manage"
                                />
                            }
                            label="Send to ConnectWise Manage"
                        />
                        <br />
                    </IntegrationEnabled>
                </Grid>
                {type !== 'personal' && !cwManage && this.renderUserSelect()}
                <Grid item xs={12}>
                    <Typography color="textSecondary" fontWeight={600}>
                        {t('Common.notes')}
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        variant="outlined"
                        multiline
                        minRows={2}
                        autoFocus={false}
                        type="text"
                        onChange={(e) =>
                            this.setState({ notes: e.target.value })
                        }
                        fullWidth
                        autoComplete="off"
                        value={notes}
                    />
                </Grid>
            </Grid>
        );
    };

    renderModal = () => {
        const { t } = this.props;
        const { open } = this.state;

        return (
            <Modal
                onClose={() => this.setState({ open: false })}
                open={open}
                aria-labelledby="simple-dialog-title"
                TransitionComponent={Transition}
                title={t('Reports.save_and_schedule')}
            >
                <ModalBody>{this.renderContent()}</ModalBody>
                <ModalFooter>
                    <Button
                        onClick={() => this.setState({ open: false })}
                        variant="outlined"
                        color="primary"
                    >
                        {t('Common.close')}
                    </Button>
                    <Button
                        onClick={() => this.save()}
                        color="primary"
                        variant="contained"
                    >
                        {t('Reports.save_and_schedule')}
                    </Button>
                </ModalFooter>
            </Modal>
        );
    };

    render() {
        const { loaded, open } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <>
                {this.renderButton()}
                {open && this.renderModal()}
            </>
        );
    }
}

export default withRouter(withTranslation()(SaveAndSchedule));
